import {
  Alert, Box, Button, ColumnLayout, Container, Form, FormField,
  Header, Select, SpaceBetween,
} from "@cloudscape-design/components";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "./auth";
import { getAwsCreds } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { API_BASE_URL } from "./config";
import { useDocumentTitle } from "./useDocumentTitle";

interface Stakeholder {
  documentId: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface KiroPackage {
  name: string;
  credits: number;
  baseCost: number;
  feePct: number;
  totalCost: number;
}

interface OptionType {
  label: string;
  value: string;
}

export default function RequestKiroAccessPage() {
  useDocumentTitle("Request Kiro Access");
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [packages, setPackages] = useState<KiroPackage[]>([]);
  const [selectedStakeholder, setSelectedStakeholder] = useState<OptionType | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<OptionType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: "success" | "error"; content: string } | null>(null);

  const apiKey = getAuthToken();
  const navigate = useNavigate();

  const fetchStakeholders = useCallback(() => {
    if (!apiKey) return;
    apiFetch(`/stakeholders`)
      .then((res) => { if (!res.ok) throw new Error(`API error: ${res.status}`); return res.json(); })
      .then((json) => setStakeholders(json.data.map((item: any) => ({
        documentId: item.documentId, firstName: item.firstName,
        lastName: item.lastName, email: item.email,
      }))))
      .catch((err) => setAlert({ type: "error", content: err.message }));
  }, [apiKey]);

  const fetchPackages = useCallback(() => {
    if (!apiKey) return;
    // Pricing (incl. the fee) lives ONLY server-side; read it from the packages endpoint.
    apiFetch(`/kiro-subscription-requests/packages`)
      .then((res) => { if (!res.ok) throw new Error(`API error: ${res.status}`); return res.json(); })
      .then((data: KiroPackage[]) => setPackages(data))
      .catch((err) => setAlert({ type: "error", content: err.message }));
  }, [apiKey]);

  useEffect(() => { fetchStakeholders(); fetchPackages(); }, [fetchStakeholders, fetchPackages]);

  const stakeholderOptions: OptionType[] = stakeholders.map((s) => ({
    label: `${s.firstName} ${s.lastName} (${s.email})`,
    value: s.documentId,
  }));

  const packageOptions: OptionType[] = packages.map((p) => ({
    label: `${p.name} — ${p.credits} credits — $${p.totalCost}/mo`,
    value: p.name,
  }));

  const currentPackage = packages.find((p) => p.name === selectedPackage?.value);

  const handleSubmit = async () => {
    setAlert(null);
    if (!selectedStakeholder || !selectedPackage) {
      setAlert({ type: "error", content: "Select a stakeholder and a package." });
      return;
    }
    setSubmitting(true);
    try {
      // Kiro operations use the organization main account's AWS credentials.
      const orgRes = await apiFetch(`/accounts/get-org-account`);
      if (!orgRes.ok) {
        const err = await orgRes.json();
        throw new Error(err.error?.message || `Could not resolve organization main account (${orgRes.status})`);
      }
      const orgAccount = await orgRes.json();

      const creds = getAwsCreds(orgAccount.alias);
      if (!creds) {
        throw new Error(
          `No AWS credentials found for the organization main account "${orgAccount.alias}". Add them on the Accounts page.`
        );
      }

      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/kiro-subscription-requests/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-AWS-Access-Key-Id": creds.accessKeyId,
          "X-AWS-Secret-Access-Key": creds.secretAccessKey,
          "X-AWS-Session-Token": creds.sessionToken,
        },
        body: JSON.stringify({
          stakeholderId: selectedStakeholder.value,
          packageName: selectedPackage.value,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      setAlert({
        type: "success",
        content: "Kiro subscription request submitted. Validation against IAM Identity Center is in progress.",
      });
      setSelectedStakeholder(null);
      setSelectedPackage(null);
    } catch (e: any) {
      // Surfaces the IAM Identity Center email-mismatch error message when validation fails.
      setAlert({ type: "error", content: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SpaceBetween size="l">
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}
          header={alert.type === "success" ? "Request submitted" : "Request failed"}>
          {alert.content}
        </Alert>
      )}
      <Form
        header={<Header variant="h1" description="Request a Kiro subscription for a stakeholder.">Request Kiro Access</Header>}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => navigate("/kiro-access")}>Cancel</Button>
            <Button variant="primary" loading={submitting} onClick={handleSubmit}>Submit request</Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Subscription details</Header>}>
            <SpaceBetween size="m">
              <FormField label="Stakeholder" description="The person who will receive the Kiro subscription.">
                <Select
                  selectedOption={selectedStakeholder}
                  onChange={({ detail }) => setSelectedStakeholder(detail.selectedOption as OptionType)}
                  options={stakeholderOptions}
                  placeholder="Select a stakeholder"
                  filteringType="auto"
                />
              </FormField>
              <FormField label="Package" description="Kiro package. The monthly total includes the standard fee.">
                <Select
                  selectedOption={selectedPackage}
                  onChange={({ detail }) => setSelectedPackage(detail.selectedOption as OptionType)}
                  options={packageOptions}
                  placeholder="Select a package"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          {currentPackage && (
            <Container header={<Header variant="h2">Summary</Header>}>
              <ColumnLayout columns={2} variant="text-grid">
                <div key="package">
                  <Box variant="awsui-key-label">Package</Box>
                  <div>{currentPackage.name}</div>
                </div>
                <div key="credits">
                  <Box variant="awsui-key-label">Credits</Box>
                  <div>{currentPackage.credits}</div>
                </div>
                <div key="total">
                  <Box variant="awsui-key-label">Total (monthly)</Box>
                  <div>${currentPackage.totalCost}</div>
                </div>
              </ColumnLayout>
            </Container>
          )}
        </SpaceBetween>
      </Form>
    </SpaceBetween>
  );
}
